import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import MemberSelector from './MemberSelector';
import AmountInput from './AmountInput';
import type { Member, ContributionFormData, ValidationErrors, QuickAmountSuggestion } from '../types';

interface ContributionFormProps {
  groupId: string;
  members: Member[];
  onSuccess: () => void;
  onCancel: () => void;
  batchMode?: boolean;
  onAddToBatch?: (data: ContributionFormData) => void;
  initialData?: ContributionFormData;
}

const ContributionForm: React.FC<ContributionFormProps> = ({ 
  groupId, 
  members, 
  onSuccess, 
  onCancel,
  batchMode = false,
  onAddToBatch,
  initialData
}) => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ContributionFormData>(
    initialData || {
      memberId: '',
      amount: '',
      date: new Date(),
      notes: '',
    }
  );
  const [errors, setErrors] = useState<ValidationErrors>({});

  // Sample suggestions - could be fetched from history or settings
  const suggestions: QuickAmountSuggestion[] = [
    { amount: 500, label: '500 VT', frequency: 0 },
    { amount: 1000, label: '1000 VT', frequency: 0 },
    { amount: 2000, label: '2000 VT', frequency: 0 },
    { amount: 5000, label: '5000 VT', frequency: 0 },
  ];

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {};
    if (!formData.memberId) newErrors.memberId = 'Please select a member';
    if (!formData.amount || isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate() || !user) return;

    // If onAddToBatch is provided (for batch mode or updates), use it
    if (onAddToBatch) {
      onAddToBatch(formData);
      // Reset form for next entry (only if not editing)
      if (!initialData) {
        setFormData({
          memberId: '',
          amount: '',
          date: new Date(),
          notes: '',
        });
        setErrors({});
      }
      return;
    }

    // Direct submission (for admins or non-batch mode)
    setLoading(true);
    try {
      const amount = parseFloat(formData.amount);
      
      // Check if user is a clerk in any group (simplified check, usually we'd check specifically for this group)
      const isClerk = userProfile?.group_roles?.includes('clerk');
      const status = isClerk ? 'pending_approval' : 'approved';

      const { error } = await supabase.from('contributions').insert({
        group_id: groupId,
        member_id: formData.memberId,
        amount: amount,
        contribution_date: formData.date.toISOString(),
        notes: formData.notes.trim() || null,
        status: status,
        recorded_by: user.id,
      });

      if (error) throw error;

      onSuccess();
    } catch (error: any) {
      console.error('Error recording contribution:', error);
      alert('Failed to record contribution: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <MemberSelector
        members={members}
        selectedMemberId={formData.memberId}
        onSelect={(id) => setFormData({ ...formData, memberId: id })}
        error={errors.memberId}
        disabled={loading}
      />

      <AmountInput
        value={formData.amount}
        onChange={(val) => setFormData({ ...formData, amount: val })}
        error={errors.amount}
        disabled={loading}
        minAmount={1}
        maxAmount={1000000}
        suggestions={suggestions}
      />

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Savings Date
        </label>
        <div className="relative">
          <Icon 
            name="Calendar" 
            size={18} 
            className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="date"
            value={formData.date.toISOString().split('T')[0]}
            onChange={(e) => setFormData({ ...formData, date: new Date(e.target.value) })}
            max={new Date().toISOString().split('T')[0]}
            disabled={loading}
            className="w-full pl-12 pr-4 py-3 rounded-md border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Select the date this saving was collected.
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-2">
          Notes (Optional)
        </label>
        <textarea
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          placeholder="Add any additional details..."
          disabled={loading}
          className="w-full px-4 py-3 rounded-md border border-input bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring min-h-[100px]"
        />
      </div>

      <div className="flex gap-3 pt-4">
        {batchMode ? (
          <>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={loading}
            >
              Done Adding
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={loading}
              iconName="Plus"
              iconPosition="left"
            >
              Add to Batch
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              onClick={onCancel}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1"
              disabled={loading}
              iconName={loading ? 'Loader2' : 'Check'}
              iconPosition="left"
            >
              {loading ? 'Recording...' : 'Record Savings'}
            </Button>
          </>
        )}
      </div>
    </form>
  );
};

export default ContributionForm;
